import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import isEqual from 'react-fast-compare';
import _ from 'lodash';
import { Intent, ButtonGroup, Button, MenuItem } from '@blueprintjs/core';
import { Tooltip2 } from '@blueprintjs/popover2';
import { MultiSelect2 } from '@blueprintjs/select';
import cx from 'classnames';

import cls from './MultiSelect.module.css';

export default class extends PureComponent {
  static propTypes = {
    'items': PropTypes.array,
    'selectedItemIds': PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
    'value': PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    'idField': PropTypes.string,
    'labelField': PropTypes.string,
    'minimal': PropTypes.bool,
    'outlined': PropTypes.bool,
    'small': PropTypes.bool,
    'large': PropTypes.bool,
    'enableSelectAll': PropTypes.bool,
    'enableSelectNone': PropTypes.bool,
    'intent': PropTypes.string,
    'icon': PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    'rightIcon': PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    'data-test': PropTypes.string,
    'buttonProps': PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
    'onChange': PropTypes.func,
    'containerClassName': PropTypes.string,
  };

  static defaultProps = {
    selectedItemIds: [],
    idField: 'id',
    labelField: 'label',
    minimal: false,
    outlined: false,
    small: false,
    large: false,
    enableSelectAll: false,
    enableSelectNone: false,
    intent: Intent.NONE,
    rightIcon: 'chevron-down',
    buttonProps: (selectedItems, items) => ({ text: `${selectedItems.length}/${items.length} items selected` }),
    onChange: () => {},
  };

  state = {
    items: [],
    selectedItems: [],
  };

  selectRef = React.createRef();

  componentDidMount() {
    this.onItemsChange();
  }

  componentDidUpdate(prevProps, prevState) {
    const { items, selectedItemIds } = this.props;
    const { items: prevItems, selectedItemIds: prevSelectedItemIds } = prevProps;
    const { selectedItems } = this.state;
    const { selectedItems: prevSelectedItems } = prevState;

    const didItemsChange = items && !isEqual(items, prevItems);
    const didSelectedItemIdsChange = !isEqual(selectedItemIds, prevSelectedItemIds);
    const didActiveItemsChange = !isEqual(selectedItems, prevSelectedItems);

    if (didItemsChange || didSelectedItemIdsChange) {
      this.onItemsChange();
    }

    if (didActiveItemsChange) {
      this.props.onChange(selectedItems);
    }
  }

  isItemSelected = id => {
    const { idField } = this.props;
    const { selectedItems } = this.state;
    return Boolean(selectedItems.find(item => item[idField] === id));
  };

  filterItem = (query, item) => {
    const { labelField } = this.props;

    const normalizedText = item[labelField].toLowerCase();
    const normalizedQuery = query.toLowerCase();

    return normalizedText.indexOf(normalizedQuery) >= 0;
  };

  buildButtonProps = () => {
    const { items, buttonProps } = this.props;
    const { selectedItems } = this.state;

    if (typeof buttonProps === 'function') {
      return buttonProps(selectedItems, items);
    }

    return buttonProps;
  };

  onItemsChange = () => {
    const { items, selectedItemIds, idField } = this.props;
    const selectedItems = items.filter(item => selectedItemIds.includes(item[idField])) ?? [];

    this.setState({
      items,
      selectedItems,
    });
  };

  onItemSelect = selectedItem => {
    const { idField } = this.props;

    if (this.isItemSelected(selectedItem[idField])) {
      this.setState(state => ({
        selectedItems: state.selectedItems.filter(item => item[idField] !== selectedItem[idField]),
      }));
      return;
    }

    this.setState(state => ({
      selectedItems: [...state.selectedItems, selectedItem],
    }));
  };

  onAllItemsSelect = () => {
    const { items } = this.props;

    this.setState({
      selectedItems: items,
    });
  };

  onItemsClear = () => {
    this.setState({
      selectedItems: [],
    });
  };

  renderItem = (item, { modifiers, handleClick }) => {
    const { idField, labelField, 'data-test': dataTest } = this.props;

    if (!modifiers.matchesPredicate) {
      return null;
    }

    return (
      <MenuItem
        shouldDismissPopover
        active={modifiers.active}
        icon={this.isItemSelected(item[idField]) ? 'tick' : 'blank'}
        text={item[labelField]}
        data-cy={`${dataTest}-menu-item`}
        onClick={handleClick}
        key={item[idField]}
      />
    );
  };

  renderTag = () => null;

  render() {
    const {
      'items': originalItems,
      selectedItemIds,
      labelField,
      minimal,
      outlined,
      small,
      large,
      enableSelectAll,
      enableSelectNone,
      intent,
      icon,
      rightIcon,
      'data-test': dataTest,
      onChange,
      containerClassName,
      ...restProps
    } = this.props;
    const { items, selectedItems } = this.state;

    return (
      <div className={cx(cls.container, containerClassName)}>
        <MultiSelect2
          fill
          {...restProps}
          placeholder=""
          resetOnQuery={false}
          items={items}
          selectedItems={selectedItems}
          itemPredicate={this.filterItem}
          itemRenderer={this.renderItem}
          tagRenderer={this.renderTag}
          onItemSelect={this.onItemSelect}
          itemsEqual={labelField}
          noResults={<MenuItem disabled text="No matching results." data-test={`${dataTest}-empty-menu-item`} />}
          popoverProps={{
            minimal: true,
            captureDismiss: true,
          }}
          className={cls.multiSelect}
          ref={this.selectRef}
        />

        <ButtonGroup large={large} data-test={`${dataTest}-select`}>
          <Button
            {...this.buildButtonProps()}
            onMouseDown={() => {
              if (this.selectRef.current.state.isOpen) {
                return;
              }
              setTimeout(() => {
                this.selectRef.current.input.click();
              }, 0);
            }}
            outlined={outlined}
            large={large}
            intent={intent}
            rightIcon={rightIcon}
            data-test={`${dataTest}-button-dropdown`}
            data-cy={`${dataTest}-button-dropdown`}
          />
          {enableSelectAll && (
            <Tooltip2 content="Select all" placement="top">
              <Button
                outlined={outlined}
                intent={intent}
                icon="multi-select"
                onClick={this.onAllItemsSelect}
                data-test={`${dataTest}-button-select-all`}
              />
            </Tooltip2>
          )}
          {enableSelectNone && (
            <Tooltip2 content="Select none" placement="top">
              <Button
                outlined={outlined}
                intent={intent}
                icon="cross"
                onClick={this.onItemsClear}
                data-test={`${dataTest}-button-select-none`}
                data-cy={`${dataTest}-button-select-none`}
              />
            </Tooltip2>
          )}
        </ButtonGroup>
      </div>
    );
  }
}
